import { Placement } from '@popperjs/core';
import classNames from 'classnames';
import React, { FC, MouseEvent, useRef, useState } from 'react';
import { EventBus } from 'src/common/EventBus';

import { Journey } from 'src/common/interfaces/journey.interface';
import { Workspace } from 'src/common/interfaces/workspace.interface';
import { IconButton } from 'src/common/icon-button';
import { TooltipComponent } from 'src/common/tooltip/component';
import { DotMenuStrongIcon, LinkStrongIcon, PreviewStrongIcon } from 'src/monet/icons';
import { DashboardHomeCardOptions } from '../card-options';
import { DashboardHomeJourneysCopyLinksMenu } from './copy-links-menu';
import { DashboardHomeJourneysPreviewLinksMenu } from './preview-links-menu';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { isJourneyAccessible } from 'src/utils/journey-access';
import { useJourneys } from 'src/store/journeys';

interface DashboardHomeJourneysActionsProps {
  journey: Journey;
  expanded: boolean;
  rowExpanderProps: any;
}

export const DashboardHomeJourneyActions: FC<DashboardHomeJourneysActionsProps> = ({
  journey,
  expanded,
  rowExpanderProps,
}) => {
  const [keepMenuOpen, setKeepMenuOpen] = useState(false);
  const menuPlacementRef = useRef<Placement>();
  const { subscriptionPlanType, isOnTrial } = useCurrentSubscription();
  const { showUpgradeModal } = useFeatureRestrictions();
  const journeys = useJourneys((state) => state.journeys);

  const onMenuOpen = (placement?: Placement) => {
    if (placement) {
      menuPlacementRef.current = placement;
    }
    setKeepMenuOpen(true);
  };

  const onMenuClose = () => {
    setKeepMenuOpen(false);
  };

  const onJourneyLinkCopied = () => {
    EventBus.emit('home:journey-link-copy');
  };

  const onDeleteJourneyConfirmed = (uuid: string) => {
    EventBus.emit('home:journey-delete', uuid);
  };

  const onDuplicateJourneyConfirmed = (uuid: string, detach_synced_step?: boolean) => {
    EventBus.emit('home:journey-duplicate', { uuid, detach_synced_step });
  };

  const onWorkspaceChanged = (workspace: Workspace) => {
    EventBus.emit('home:journey-move', {
      workspaceId: workspace.id,
      uuid: journey.uuid,
    });
  };

  const onStatusChanged = (status: string) => {
    EventBus.emit('home:journey-status', {
      status: status,
      uuid: journey.uuid,
    });
  };

  const onSocialSharingChanged = (social_sharing: boolean) => {
    EventBus.emit('home:journey-is-sharing', {
      social_sharing: social_sharing,
      uuid: journey.uuid,
    });
  };

  const onNotificationChanged = (followed: boolean) => {
    EventBus.emit('home:journey-notification', {
      isFollowed: followed,
      uuid: journey.uuid,
    });
  };

  const onSessionNotificationChange = (followed: boolean) => {
    EventBus.emit('home:journey-session-notification', {
      isFollowed: followed,
      uuid: journey.uuid,
    });
  };

  const stopPropagation = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  // Check if journey is accessible for operations (excluding delete)
  const isJourneyAccessibleForOperations = isJourneyAccessible(journey, journeys, subscriptionPlanType, isOnTrial);

  // Handler for restricted journey operations
  const handleRestrictedOperation = (operationName: string) => {
    showUpgradeModal(
      'unlimited_journeys',
      `You have reached your journey limit on the Start plan. Upgrade to Grow plan to ${operationName} this journey and access all your journeys.`,
      'Upgrade to Access All Journeys'
    );
  };

  return (
    <div
      className={classNames(
        'flex grow-0 items-center space-x-sm transition opacity-0 group-hover:opacity-100',
        keepMenuOpen || expanded ? 'opacity-100' : 'opacity-0'
      )}
      onClick={stopPropagation}
    >
      <DashboardHomeJourneysCopyLinksMenu
        journey={journey}
        onOpen={onMenuOpen}
        onClose={onMenuClose}
        onCopy={onJourneyLinkCopied}
        renderMenuOrigin={(onClick) => (
          <TooltipComponent placement='top' tooltipContent='Copy URL'>
            <IconButton 
              onClick={isJourneyAccessibleForOperations ? onClick : () => handleRestrictedOperation('copy links for')}
            >
              <LinkStrongIcon />
            </IconButton>
          </TooltipComponent>
        )}
      />
      <DashboardHomeJourneysPreviewLinksMenu
        journey={journey}
        onOpen={onMenuOpen}
        onClose={onMenuClose}
        renderMenuOrigin={(onClick) => (
          <TooltipComponent placement='top' tooltipContent='Preview'>
            <IconButton 
              onClick={isJourneyAccessibleForOperations ? onClick : () => handleRestrictedOperation('preview')}
            >
              <PreviewStrongIcon />
            </IconButton>
          </TooltipComponent>
        )}
      />
      <DashboardHomeCardOptions
        journey={journey}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        onDeleteJourneyConfirm={onDeleteJourneyConfirmed}
        onNotificationChange={onNotificationChanged}
        onSessionNotificationChange={onSessionNotificationChange}
        onStatusChange={onStatusChanged}
        onSocialSharingChange={onSocialSharingChanged}
        onWorkspaceChange={onWorkspaceChanged}
        onDuplicateJourneyConfirm={onDuplicateJourneyConfirmed}
        nestedMenuPlacement={menuPlacementRef.current}
        isJourneyAccessible={isJourneyAccessibleForOperations}
        onRestrictedOperation={handleRestrictedOperation}
        renderMenuOrigin={(onClick) => (
          <div onClick={onClick}>
            <DotMenuStrongIcon />
          </div>
        )}
      />
    </div>
  );
};
